import { matchBookingItems } from '../utils/booking';
import { dateFormatForAPI, formatDate } from '../utils/date';
import { Day, RecurrenceOptions } from './recurrenceRuleBuilder';

export type RecurrencePayload = {
  type: RecurrenceOptions['type'];
  everyNumberOf?: number;
  weekNumber?: number;
  weekDay?: Day;
  weekDays?: Array<Day>;
  number?: number;
  endDate?: string;
  count?: number;
};

type BookingItemPayload = {
  id?: number;
  date: string;
  startTime: string;
  endTime: string;
  spaceId: number;
};

type BasketSlotsPayload = {
  date: string;
  venueId: number;
  clientServiceId: number;
  bookingHostId: number;
  spaceIds: Array<number>;
  slots: { start: string; finish: string }[];
  recurrence?: RecurrencePayload;
};

type BasketBookingItemsPayload = {
  date: string;
  venueId: number;
  clientServiceId: number;
  bookingHostId: number;
  bookingItems: BookingItemPayload[];
};

type BasketPayload = BasketSlotsPayload | BasketBookingItemsPayload;

const monToFri = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const formatRecurrencePayload = ({
  type = 'daily',
  everyNumberOf = 1,
  weekNumber,
  weekDay,
  weekDays = [],
  nthDayOfMonth,
  dayOfMonthRange,
  dayRange,
  rangeEndType = 'count',
  endDate,
  count = 1,
}: RecurrenceOptions): RecurrencePayload =>
  ({
    type,
    ...(type === 'daily'
      ? {
          ...(dayRange === 'weekdays'
            ? { weekDays: monToFri, everyNumberOf: 1 }
            : { everyNumberOf }),
        }
      : {}),
    ...(type === 'weekly' ? { everyNumberOf, weekDays } : {}),
    ...(type === 'monthly'
      ? {
          everyNumberOf: 1,
          ...(dayOfMonthRange === 'nthDayOfMonth'
            ? { number: Number(nthDayOfMonth) }
            : { weekNumber, weekDay }),
        }
      : {}),
    ...(rangeEndType === 'date'
      ? { endDate: formatDate(endDate, dateFormatForAPI) }
      : { count }),
  }) as RecurrencePayload;

interface Slot {
  id?: number;
  startTime: string;
  endTime: string;
}

interface BookingItems {
  [date: string]: { [space: string]: Slot[] };
}

export interface FormatBasketOptions {
  date: Date;
  venueId: number;
  spaceIds: number[];
  slots: Slot[];
  clientServiceId: number;
  bookingHostId: number;
  recurrence?: RecurrenceOptions;
  bookingItems?: BookingItems;
  initialBookingItems?: BookingItems;
}

const fixSlot = ({ startTime, endTime, ...rest }: Slot) => ({
  start: startTime,
  finish: endTime,
  ...rest,
});

const extractSpaceId = (value: string): number => {
  const [, id] = /^s(\d+)$/.exec(value)!;
  return Number(id);
};

export const formatBookingItems = (items: BookingItems): BookingItemPayload[] =>
  Object.keys(items)
    .flatMap(date =>
      Object.keys(items[date]).flatMap(spaceId =>
        items[date][spaceId].map(({ id, startTime, endTime }) => ({
          ...(id === undefined ? {} : { id }),
          date,
          startTime,
          endTime,
          spaceId: extractSpaceId(spaceId),
        }))
      )
    )
    .filter(({ spaceId }) => !Number.isNaN(spaceId));

export const formatBasketPayload = ({
  date,
  venueId,
  spaceIds,
  slots,
  clientServiceId,
  bookingHostId,
  recurrence,
  bookingItems,
  initialBookingItems = {},
}: FormatBasketOptions): BasketPayload => ({
  date: formatDate(date, dateFormatForAPI),
  venueId,
  clientServiceId,
  bookingHostId,
  recurrence: recurrence
    ? formatRecurrencePayload(recurrence)
    : (null as unknown as undefined),
  ...(bookingItems && Object.keys(bookingItems).length > 0
    ? {
        bookingItems: matchBookingItems(
          formatBookingItems(bookingItems) as Parameters<
            typeof matchBookingItems
          >[0],
          formatBookingItems(initialBookingItems) as Parameters<
            typeof matchBookingItems
          >[0]
        ),
      }
    : {
        spaceIds,
        slots: slots.map(fixSlot),
        ...(recurrence
          ? { recurrence: formatRecurrencePayload(recurrence) }
          : {}),
      }),
});
